import { has } from 'es-toolkit/compat';
import { Procedure } from 'interfaces/procedure';
import { ProceduresFieldsContext } from 'interfaces/procedure/fields/helpers';
import { Slide } from 'interfaces/slide';
import {
  cloneDeep,
  compact,
  every,
  find,
  findIndex,
  first,
  includes,
  indexOf,
  isNil,
  join,
  map,
  size,
  slice,
  some,
  toLower,
  trim,
} from 'lodash';
import papaparse from 'papaparse';
import readXlsxFile, { readSheetNames, Row } from 'read-excel-file';
import { Cell } from 'read-excel-file/types';
import { reduceExtraSpaces } from 'utils/helpers';
import { EnumDisplayNames } from 'utils/queryHooks/uiConstantsHooks';
import {
  BOOLEAN_OPTIONS,
  LOWER_CASE_MANDATORY_HEADERS,
  LOWER_CASE_SLIDE_IDENTIFIER_HEADER_OPTIONS,
  NEW_CASE_HEADER,
  SLIDE_IDENTIFIER_HEADER_OPTIONS,
} from './consts';
import { decodeManifestWorkbookContext } from './manifestWorkbookContext';
import type { ManifestDraftIssue } from './dialogState';

export class ManifestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ManifestError';
  }
}

const isCsvFile = (file: File) => file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');

const readCsvRows = async (file: File): Promise<Row[]> => {
  const rows = await new Promise<string[][]>((resolve, reject) => {
    papaparse.parse<string[]>(file, {
      complete: ({ data, errors }) => {
        // Papa Parse reports UndetectableDelimiter even for valid files; only quote errors corrupt rows.
        const quoteError = find(errors, { type: 'Quotes' });
        if (quoteError) {
          const rowNumber = isNil(quoteError.row) ? '' : ` near row ${quoteError.row + 1}`;
          reject(new ManifestError(`The CSV file could not be read${rowNumber}: ${quoteError.message}`));
          return;
        }
        resolve(data);
      },
      error: reject,
    });
  });
  if (rows[0]?.[0]) {
    rows[0][0] = rows[0][0].replace(/^\uFEFF/, '');
  }
  return rows;
};

const isBlankCell = (cell: Cell | undefined) => isNil(cell) || (typeof cell === 'string' && trim(cell) === '');

// Format readers only decode the file; every format then shares one cell/row normalization.
export const readManifestRows = async (file: File): Promise<Row[]> => {
  const rows = isCsvFile(file) ? await readCsvRows(file) : await readXlsxFile(file);

  return compact(
    map(rows, (row) => {
      const normalizedRow = map(row, (cell) => (isBlankCell(cell) ? null : cell));
      return every(normalizedRow, isNil) ? null : normalizedRow;
    })
  );
};

export const readManifestWorkbookContext = async (file: File) => {
  if (isCsvFile(file)) return decodeManifestWorkbookContext(undefined);
  try {
    const sheetNames = await readSheetNames(file);
    if (!sheetNames.includes('Context')) return decodeManifestWorkbookContext(undefined);
    return decodeManifestWorkbookContext(await readXlsxFile(file, { sheet: 'Context', trim: false }));
  } catch {
    return decodeManifestWorkbookContext([]);
  }
};

const NEW_CASE_COLUMN_HEADERS = [NEW_CASE_HEADER.toLowerCase(), 'new case'];

export const readExplicitNewCaseIntent = (headers: string[], row: Row): boolean => {
  const columnIndex = findIndex(headers, (header) => NEW_CASE_COLUMN_HEADERS.includes(header.toLowerCase()));
  if (columnIndex < 0) return false;
  const value = getNormalizedOptionalCellString(row[columnIndex]);
  if (!value) return false;
  if (value.toLowerCase() === 'new case') return true;
  throw new ManifestError('Invalid New case value. Use New case or leave the cell blank.');
};

export const validateMandatoryHeaders = (headers: string[]): boolean => {
  const loweredCaseHeaders = map(headers, (header) => header.toLowerCase());
  if (
    !some(LOWER_CASE_SLIDE_IDENTIFIER_HEADER_OPTIONS, (slide_identifier) =>
      includes(loweredCaseHeaders, slide_identifier)
    )
  ) {
    throw new ManifestError(
      `Missing slide identifier, please add one of the following options: ${join(
        SLIDE_IDENTIFIER_HEADER_OPTIONS,
        ', '
      )}`
    );
  }
  return every(LOWER_CASE_MANDATORY_HEADERS, (header) => {
    if (!headers.includes(header)) {
      throw new ManifestError(`Missing mandatory header: ${header}`);
    }
    return true;
  });
};

export const getBooleanValue = (value: string | null): boolean | null => {
  if (value === null) {
    return null;
  }

  if (!includes(BOOLEAN_OPTIONS, value.toLowerCase())) {
    throw new ManifestError('Invalid boolean value: ' + value);
  }

  return includes(['y', 'yes'], value.toLowerCase());
};

const getNormalizedOptionalCellString = (value: Cell | undefined): string | null => {
  if (!value) {
    return null;
  }

  const normalizedValue = reduceExtraSpaces(value.toString());

  return normalizedValue ? normalizedValue : null;
};

// Enum cells may hold the display label (template dropdowns) or the stored value.
const getEnumValueFromCell = (
  enumDisplayNames: EnumDisplayNames | undefined,
  enumType: string,
  cellValue: string | null,
  fieldName: string,
  preserveInvalidValue = false
): string | null => {
  if (!cellValue) {
    return null;
  }
  const normalizedCellValue = toLower(cellValue);
  const option = find(
    enumDisplayNames?.[enumType],
    ({ label, value }) => toLower(label) === normalizedCellValue || toLower(value) === normalizedCellValue
  );
  if (!option) {
    if (preserveInvalidValue) return cellValue;
    throw new ManifestError(`Invalid ${fieldName}. Choose a current option and upload again.`);
  }
  return option.value;
};

export const getCaseByFileName = (fileName: string, procedures: Procedure[]) => {
  if (!fileName) {
    return null;
  }
  const trimmedFileName = reduceExtraSpaces(fileName);
  const casesByMatchingSlideId = new Map<string, Procedure>();
  for (const procedure of procedures) {
    for (const slide of procedure.slides || []) {
      if (reduceExtraSpaces(slide.originalFileName) === trimmedFileName) {
        if (!casesByMatchingSlideId.has(slide.id)) casesByMatchingSlideId.set(slide.id, procedure);
        if (casesByMatchingSlideId.size > 1) {
          throw new ManifestError('File name matches more than one uploaded slide. Enter a Slide ID and upload again.');
        }
      }
    }
  }
  return first([...casesByMatchingSlideId.values()]);
};

export const getSlideByFileName = (fileName: string, procedure: Procedure) => {
  if (!fileName) {
    return null;
  }
  const trimmedFileName = reduceExtraSpaces(fileName);
  const caseWithTrimmedFileNames = {
    ...procedure,
    slides: map(procedure?.slides, (slide) => ({
      ...slide,
      originalFileName: reduceExtraSpaces(slide.originalFileName),
    })),
  };
  const searchedSlide = find(caseWithTrimmedFileNames.slides, { originalFileName: trimmedFileName });
  return find(procedure.slides, { id: searchedSlide?.id });
};

export const getCaseBySlideId = (slideId: string, procedures: Procedure[]) => {
  return find(procedures, (procedure) => some(procedure?.slides, { id: slideId }));
};

// A row with both identifiers resolves by Slide ID; only a File Name that disagrees with that slide is worth surfacing.
export const getSlideIdentifierMismatchWarning = (
  headers: string[],
  row: Row,
  slide: Pick<Slide, 'id' | 'originalFileName'> | undefined,
  rowNumber: number
): string | null => {
  const slideIdIndex = indexOf(headers, 'slide id');
  const fileNameIndex = indexOf(headers, 'file name');
  if (slideIdIndex < 0 || fileNameIndex < 0 || !slide?.originalFileName) return null;
  if (!getNormalizedOptionalCellString(row[slideIdIndex])) return null;
  const providedFileName = getNormalizedOptionalCellString(row[fileNameIndex]);
  if (!providedFileName || providedFileName === reduceExtraSpaces(slide.originalFileName)) return null;
  return `Row ${rowNumber}: File Name "${providedFileName}" does not match the original file name "${slide.originalFileName}" of Slide ID ${slide.id}. Using the Slide ID.`;
};

const DEFAULT_CANCER_TYPE_ID = 9; // N/A

export const getCaseFromRowCombinedWithRealData = (
  procedureFieldContext: ProceduresFieldsContext,
  headers: string[],
  row: Row,
  labId: string,
  studyId: string,
  index: number,
  allCasesAndMockCases: Procedure[],
  notEditableSlidesMockCases: Procedure[],
  preserveInvalidEditableValues = false,
  onRowIssue?: (issue: ManifestDraftIssue) => void
): Procedure => {
  const { allCancerTypes, biopsySiteTypes, stainTypesNotDeprecated, enumDisplayNames } = procedureFieldContext;

  const caseLabelCell: Cell = row[indexOf(headers, 'case id')];
  const caseLabel = getNormalizedOptionalCellString(caseLabelCell);

  const fileNameCell: Cell = row[indexOf(headers, 'file name')];
  let fileName: string = getNormalizedOptionalCellString(fileNameCell);
  const slideIdCell: Cell = row[indexOf(headers, 'slide id')];
  let slideId: string = getNormalizedOptionalCellString(slideIdCell);

  const stainTypeCell: Cell = row[indexOf(headers, 'stain type')];
  const stainType = getNormalizedOptionalCellString(stainTypeCell);
  const cancerTypeCell: Cell = row[indexOf(headers, 'cancer type')];
  const cancerType = getNormalizedOptionalCellString(cancerTypeCell);
  const biopsySiteCell: Cell = row[indexOf(headers, 'biopsy site')];
  const biopsySite = getNormalizedOptionalCellString(biopsySiteCell);
  const cancerSubtypeCell: Cell = row[indexOf(headers, 'cancer subtype')];
  const cancerSubtype = getNormalizedOptionalCellString(cancerSubtypeCell);
  const biopsyTypeCell: Cell = row[indexOf(headers, 'biopsy type')];
  const biopsyType = getEnumValueFromCell(
    enumDisplayNames,
    'biopsyType',
    getNormalizedOptionalCellString(biopsyTypeCell),
    'Biopsy Type',
    preserveInvalidEditableValues
  );
  const preservationMethodCell: Cell = row[indexOf(headers, 'preservation method')];
  const preservationMethod = getEnumValueFromCell(
    enumDisplayNames,
    'preservationMethod',
    getNormalizedOptionalCellString(preservationMethodCell),
    'Preservation Method',
    preserveInvalidEditableValues
  );
  const procedureTypeCell: Cell = row[indexOf(headers, 'procedure type')];
  const procedureType = getEnumValueFromCell(
    enumDisplayNames,
    'procedureType',
    getNormalizedOptionalCellString(procedureTypeCell),
    'Procedure Type',
    preserveInvalidEditableValues
  );
  const metastasisCell: Cell = row[indexOf(headers, 'metastasis (y/n)')];
  const metastasis = getNormalizedOptionalCellString(metastasisCell);
  const positiveControlCell: Cell = row[indexOf(headers, 'positive control on slide (y/n)')];
  const positiveControl = getNormalizedOptionalCellString(positiveControlCell);
  const negativeControlCell: Cell = row[indexOf(headers, 'negative control on slide (y/n)')];
  const negativeControl = getNormalizedOptionalCellString(negativeControlCell);

  const cancerTypeId = cancerType
    ? find(allCancerTypes, { displayName: cancerType })?.id || cancerType
    : DEFAULT_CANCER_TYPE_ID;

  const biopsySiteId: number | string = biopsySite
    ? find(biopsySiteTypes, { displayName: biopsySite })?.id || biopsySite
    : null;

  const stainTypeId: string = stainType
    ? find(stainTypesNotDeprecated, { displayName: stainType })?.id || stainType
    : null;

  if (!preserveInvalidEditableValues && cancerType && !has(allCancerTypes, [cancerTypeId])) {
    throw new ManifestError('Invalid Cancer Type. Choose a current option and upload again.');
  }
  if (!preserveInvalidEditableValues && biopsySite && !find(biopsySiteTypes, { id: Number(biopsySiteId) })) {
    throw new ManifestError('Invalid Biopsy Site. Choose a current option and upload again.');
  }
  if (!preserveInvalidEditableValues && stainType && !find(stainTypesNotDeprecated, { id: stainTypeId })) {
    throw new ManifestError('Invalid Stain Type. Choose a current option and upload again.');
  }

  if (!fileName && !slideId && !onRowIssue) {
    throw new ManifestError('Missing slide identifier');
  }

  let existingCase: Procedure = null;
  let existingSlide: Slide = null;

  // add slide id fetching when filename provided, and filename fetching when slide id provided
  if (slideId) {
    const notEditableSlideMockCase = find(
      notEditableSlidesMockCases,
      (mockCase) => first(mockCase?.slides)?.id === slideId
    );
    if (notEditableSlideMockCase) {
      existingCase = {
        id: notEditableSlideMockCase.id,
        labId: labId,
        label: caseLabel,
        cancerTypeId: DEFAULT_CANCER_TYPE_ID,
        slides: notEditableSlideMockCase.slides,
        studyId: studyId,
      };
      existingSlide = first(notEditableSlideMockCase.slides);
      fileName = existingSlide.originalFileName;
    } else {
      existingCase = getCaseBySlideId(slideId, allCasesAndMockCases);
      existingSlide = existingCase ? (find(existingCase.slides, { id: slideId }) as Slide) : undefined;
      fileName = existingSlide?.originalFileName || (onRowIssue ? fileName : null);
    }
  } else {
    try {
      existingCase = getCaseByFileName(fileName, allCasesAndMockCases);
    } catch (error) {
      if (!(error instanceof ManifestError) || !onRowIssue) throw error;
      onRowIssue({
        field: 'slideId',
        message: 'File name matches more than one slide. Remove this row and add the correct slide by ID.',
      });
    }
    existingSlide = existingCase ? getSlideByFileName(fileName, existingCase) : undefined;
    slideId = existingSlide?.id;
  }

  const notEditableSlide = first(
    find(notEditableSlidesMockCases, (mockCase) => first(mockCase?.slides)?.id === slideId)?.slides
  );

  const readBoolean = (field: string, value: string | null, inheritedValue?: boolean) => {
    if (!value) return inheritedValue;
    try {
      return getBooleanValue(value);
    } catch (error) {
      if (!(error instanceof ManifestError) || !onRowIssue) throw error;
      const label =
        field === 'metastasis' ? 'Metastasis' : field === 'positiveControl' ? 'Positive control' : 'Negative control';
      onRowIssue({ field, rawValue: value, message: `${label} value "${value}" is invalid. Choose Yes or No.` });
      return undefined;
    }
  };
  const procedure: Procedure = {
    id: existingCase ? existingCase.id : index,
    labId: labId,
    comments: existingCase?.comments,
    batchDate: existingCase?.batchDate,
    createdAt: existingCase?.createdAt,
    presentationInfo: existingCase?.presentationInfo,
    diagnosis: existingCase?.diagnosis,
    label: caseLabel,
    cancerTypeId: cancerTypeId,
    cancerSubtypes: cancerSubtype ? [cancerSubtype] : existingCase?.cancerSubtypes,
    slides: [
      {
        ...(notEditableSlide && !preserveInvalidEditableValues
          ? notEditableSlide
          : {
              ...(existingSlide ?? {}),
              id:
                existingSlide?.id ||
                (slideId
                  ? reduceExtraSpaces(slideId)
                  : onRowIssue
                  ? fileName
                    ? `unmatched-file:${encodeURIComponent(fileName)}`
                    : `unmatched-row-${index}`
                  : index.toString()),
              originalFileName: existingSlide?.originalFileName || fileName,
              stainingType: stainTypeId ? stainTypeId : existingSlide?.stainingType,
              biopsySiteId: biopsySiteId ? biopsySiteId : existingSlide?.biopsySiteId,
              biopsyType: biopsyType ? biopsyType : existingSlide?.biopsyType,
              preservationMethod: preservationMethod ? preservationMethod : existingSlide?.preservationMethod,
              procedureType: procedureType ? procedureType : existingSlide?.procedureType,
              negativeControl: readBoolean('negativeControl', negativeControl, existingSlide?.negativeControl),
              positiveControl: readBoolean('positiveControl', positiveControl, existingSlide?.positiveControl),
              metastasis: readBoolean('metastasis', metastasis, existingSlide?.metastasis),
            }),
      },
    ],
    clinicalData: existingCase?.clinicalData,
    internallyApproved: existingCase?.internallyApproved,
    features: existingCase?.features,
    studyId: studyId,
  };

  return procedure;
};

// This function adds the new case to the current cases list.
// If the case already exists, it will add the slide to the existing case.
// If this is a list of slides that does not exist in the database,
// it will generate a new temporary id for the slide if it's case already exists (so slides in the same case will have different ids)
// so each row in the table will have a unique id.
const addCaseToList = (newCase: Procedure, cases: Procedure[], isSlideMissing: boolean = false) => {
  const slide = first(newCase?.slides);
  const slideFileName = slide.originalFileName;
  const existingCaseIndex = findIndex(cases, { label: newCase?.label });
  if (existingCaseIndex !== -1) {
    const existingCase = cases[existingCaseIndex];
    if (find(existingCase?.slides, isSlideMissing ? { originalFileName: slideFileName } : { id: slide.id })) {
      const newWarning = `Slide ${slideFileName} in case ${existingCase?.label} already exists in the manifest. (taking first slide)`;
      return { cases, warning: newWarning };
    } else {
      // Generate a new id for the missing slide (does not exist in db) so slides in the same case will have different ids
      const slideToAdd = isSlideMissing ? { ...slide, id: (size(existingCase?.slides) + 1).toString() } : slide;

      const updatedCase = cloneDeep(existingCase);
      updatedCase.slides.push(slideToAdd);

      const newCases = [...slice(cases, 0, existingCaseIndex), updatedCase, ...slice(cases, existingCaseIndex + 1)];

      return { cases: newCases };
    }
  }
  return { cases: [...cases, newCase] };
};

// This function adds the new case to the relevant list (cases or missing slides)
export const addCaseToRelevantList = (
  newCase: Procedure,
  currentMissingSlides: Procedure[],
  currentCases: Procedure[],
  allExistingAndMockCases: Procedure[]
) => {
  const slideId = first(newCase.slides).id;
  const caseFromDatabase: Procedure = getCaseBySlideId(slideId, allExistingAndMockCases);

  if (!caseFromDatabase) {
    const { cases: newMissingSlides, warning: newWarning } = addCaseToList(newCase, currentMissingSlides, true);
    return { updatedMissingSlides: newMissingSlides, updatedCases: currentCases, warning: newWarning };
  }

  const { cases: newCases, warning: newWarning } = addCaseToList(newCase, currentCases);
  return { updatedMissingSlides: currentMissingSlides, updatedCases: newCases, warning: newWarning };
};
